import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useInstallationContext } from '../../../../context/installation';
import { UserGroupsQueryVariables, UserGroupsQuery } from '../../../../data-access/gql-types/graphql';
import { USER_GROUPS } from '../../../../data-access/queries/dashboard';
import { useCacheStore } from '../../store/cache';
import { useGroupsStore } from '../../store/groups';
import { UserGroupsCollectionSegmentPick } from '../../types';

export const useFetchGroups = () => {
  const { selectedInstallationId, skipLavvaFetch, currentInstallationLoading } = useInstallationContext();
  const { setGroups, setGroupsLoading, shouldRefetchGroups, resetRefetchGroups } = useGroupsStore((s) => s);
  const groupsSkip = useCacheStore((s) => s.groupsSkip);
  const { data, loading, refetch } = useQuery<UserGroupsQuery, UserGroupsQueryVariables>(USER_GROUPS, {
    variables: { id: selectedInstallationId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    skip: skipLavvaFetch || groupsSkip,
  });

  useEffect(() => {
    if (data?.userGroups) setGroups(data.userGroups as UserGroupsCollectionSegmentPick, true);
  }, [data?.userGroups]);

  const fetchGroups = async () => {
    resetRefetchGroups();

    try {
      const { data } = await refetch({ id: selectedInstallationId });

      if (data?.userGroups) setGroups(data.userGroups as UserGroupsCollectionSegmentPick, true);
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    if (shouldRefetchGroups && selectedInstallationId) fetchGroups();
  }, [shouldRefetchGroups, selectedInstallationId, fetchGroups]);

  useEffect(() => {
    if (currentInstallationLoading) setGroupsLoading(true);
    else {
      if (!selectedInstallationId) setGroupsLoading(false);
      else {
        setGroupsLoading(loading || skipLavvaFetch);
      }
    }
  }, [loading, skipLavvaFetch, currentInstallationLoading, selectedInstallationId]);
};
